import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { demoProfiles } from "./data/demo";
import { requireSupabase } from "./lib/supabase";
import type { Profile, SharingGroup } from "./types";

interface GroupContextValue {
  groups: SharingGroup[];
  familyMembers: Profile[];
  loading: boolean;
  error: string;
  createGroup: (name: string, description: string, memberIds: string[]) => Promise<string>;
  updateGroupMembers: (groupId: string, memberIds: string[]) => Promise<void>;
  deleteGroup: (groupId: string) => Promise<void>;
  refreshGroups: () => Promise<void>;
}

interface GroupRow {
  id: string;
  name: string;
  description: string | null;
  created_by: string;
  created_at: string;
  group_members: Array<{
    role: "owner" | "member";
    profiles: { id: string; display_name: string; avatar_url: string | null };
  }>;
}

const GroupContext = createContext<GroupContextValue | null>(null);

function fromRow(row: GroupRow): SharingGroup {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? "",
    createdBy: row.created_by,
    createdAt: row.created_at,
    members: row.group_members.map((member) => ({
      role: member.role,
      profile: {
        id: member.profiles.id,
        displayName: member.profiles.display_name,
        email: "",
        avatarUrl: member.profiles.avatar_url ?? undefined,
      },
    })),
  };
}

const demoGroups: SharingGroup[] = [
  {
    id: "demo-close-family",
    name: "Parents et sœurs",
    description: "Les recettes de tous les jours et les secrets de famille.",
    createdBy: demoProfiles[0]!.id,
    createdAt: "2026-07-24T18:00:00.000Z",
    members: [
      { profile: demoProfiles[0]!, role: "owner" },
      { profile: demoProfiles[1]!, role: "member" },
    ],
  },
];

export function GroupProvider({ children }: { children: ReactNode }) {
  const { user, isDemo } = useAuth();
  const [groups, setGroups] = useState<SharingGroup[]>(isDemo ? demoGroups : []);
  const [familyMembers, setFamilyMembers] = useState<Profile[]>(isDemo ? demoProfiles : []);
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState("");

  const refreshGroups = useCallback(async () => {
    if (!user) {
      setGroups([]);
      setFamilyMembers([]);
      setLoading(false);
      return;
    }
    if (isDemo) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const supabase = requireSupabase();
    const [groupsResult, membersResult] = await Promise.all([
      supabase
        .from("sharing_groups")
        .select(
          "id,name,description,created_by,created_at,group_members(role,profiles!group_members_user_id_fkey(id,display_name,avatar_url))",
        )
        .order("created_at", { ascending: false }),
      supabase.from("profiles").select("id,display_name,avatar_url").order("display_name"),
    ]);
    if (groupsResult.error || membersResult.error) {
      setError(groupsResult.error?.message ?? membersResult.error?.message ?? "Impossible de charger les cercles.");
    } else {
      setGroups((groupsResult.data as unknown as GroupRow[]).map(fromRow));
      setFamilyMembers(
        (membersResult.data ?? []).map((member) => ({
          id: member.id,
          displayName: member.display_name,
          email: "",
          avatarUrl: member.avatar_url ?? undefined,
        })),
      );
      setError("");
    }
    setLoading(false);
  }, [isDemo, user]);

  useEffect(() => {
    void refreshGroups();
  }, [refreshGroups]);

  const value = useMemo<GroupContextValue>(() => ({
    groups,
    familyMembers,
    loading,
    error,
    async createGroup(name, description, memberIds) {
      const cleanName = name.trim();
      if (cleanName.length < 2 || cleanName.length > 80) {
        throw new Error("Le nom du cercle doit contenir entre 2 et 80 caractères.");
      }
      if (isDemo) {
        const id = `demo-group-${Date.now()}`;
        const memberSet = new Set([user!.id, ...memberIds]);
        setGroups((current) => [{
          id,
          name: cleanName,
          description: description.trim(),
          createdBy: user!.id,
          createdAt: new Date().toISOString(),
          members: familyMembers
            .filter((member) => memberSet.has(member.id))
            .map((profile) => ({ profile, role: profile.id === user!.id ? "owner" : "member" })),
        }, ...current]);
        return id;
      }
      const { data, error: createError } = await requireSupabase().rpc("create_sharing_group", {
        group_name: cleanName,
        group_description: description.trim(),
        member_ids: memberIds,
      });
      if (createError) throw createError;
      await refreshGroups();
      return String(data);
    },
    async updateGroupMembers(groupId, memberIds) {
      if (isDemo) {
        const memberSet = new Set([user!.id, ...memberIds]);
        setGroups((current) => current.map((group) => group.id === groupId ? {
          ...group,
          members: familyMembers
            .filter((member) => memberSet.has(member.id))
            .map((profile) => ({ profile, role: profile.id === group.createdBy ? "owner" : "member" })),
        } : group));
        return;
      }
      const { error: updateError } = await requireSupabase().rpc("update_sharing_group_members", {
        target_group_id: groupId,
        member_ids: memberIds,
      });
      if (updateError) throw updateError;
      await refreshGroups();
    },
    async deleteGroup(groupId) {
      if (isDemo) {
        setGroups((current) => current.filter((group) => group.id !== groupId));
        return;
      }
      const { error: deleteError } = await requireSupabase().from("sharing_groups").delete().eq("id", groupId);
      if (deleteError) throw deleteError;
      await refreshGroups();
    },
    refreshGroups,
  }), [error, familyMembers, groups, isDemo, loading, refreshGroups, user]);

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
}

export function useGroups() {
  const value = useContext(GroupContext);
  if (!value) throw new Error("useGroups doit être utilisé dans GroupProvider.");
  return value;
}
